/* eslint-disable react/jsx-no-target-blank */
import { useMemo } from 'react';

import Link from 'next/link';

import { DesktopStyledListingImageWrapper } from '@components/ui/cards/ListingItem/ListingItemStyle';
import NextListingItemTourSlider from '@components/ui/listing/NextListingItemTourSlider';
import ListingItemTimezone from '@components/ui/listing/ListingItemTimeZone/ListingItemTimeZone';
import Button from '@components/Button/next';
import {
  getCustomTransportationIcon,
  getTransportationIcon,
} from '@components/views/tour/helpers/getTransportationIcon';
import { DurationIcon, InfoIcon, MapLocationPinIcon } from '@assets/icons';
import Typography from '@components/Typography';
import MyFavoriteComponent from '@components/views/my-favorites/MyFavoriteComponent';
import { SocialShare } from '@components/ui/social-share';
import { FavoriteItemType, TourType } from '@api/v3';
import { formatISOToDate } from '@utils/dateUtil';
import { ABROAD_TOUR_TYPE } from '@constants/constants';
import ListWorldLogo from '@components/ui/ListWorldLogo';
import { getDiscountType } from '@components/views/tour/helpers/getDiscountType';
import { getCurrencySymbol } from '@utils/utils';
import AlternativeDates from '@components/views/tour/components/next/AlternativeDates';
import PlacesToGo from '@components/views/tour/components/next/PlacesToGo';
import { SliderImage } from '@components/ui/cards/ListingItem/next/ListingItemSlider';
import Segments from '@components/views/tour/components/next/Segments';
import Promotions from '@components/ui/cards/ListingItem/next/Promotions';

import {
  DesktopWrapper,
  ZoneWrapper,
  ShareWrapperDesktop,
  TourBoardTypeWrapper,
  TourDetailsDesktop,
  TourDetailsItemDesktop,
  TourNameWrapperDesktop,
  TourTitleDesktop,
  ListingTitleWrapper,
  WorldLogoWrapperDesktop,
  BadgeWrapper,
  PriceWrapper,
  DiscountWrapperDesktop,
  PriceInfoWrapperDesktop,
  PriceSubLabel,
  DesktopButtonWrapper,
  DetailWrapper,
  ListingCardBodyWrapper,
  DesktopBottomContainer,
  BodyContainer,
  TourPromotions,
  PromotionWrapper,
  BottomWrapper,
  PromotionBottomContentWrapper,
} from './styled';
import DiscountLabel from '../DiscountLabel';
import PriceLabel from '../TourListingPrice/index';
import { CompanyPromotionItemProps } from '../PromotionGroup/PromotionItem';
import Badge from '../Badge';
import { TourListingCardProps } from '../../TourListingCard/props';
import Features from '../Features';

const DesktopTourListingCard = ({
  installments,
  fallBackImage,
  secure,
  name,
  originalPrice,
  sellingPrice,
  currency,
  discounted,
  boardType,
  discountRate,
  departureCityName,
  nightStay,
  dayStay,
  transportionTypeName,
  transportionTypeCode,
  dates,
  url,
  images,
  discountType,
  seturExecutive,
  badge,
  gtmClick,
  rate,
  videos,
  id,
  favoriteLists,
  defaultList,
  defaultListItems,
  refetchDefaultList,
  refetchFavoriteLists,
  promotion,
  features,
  placesToGo,
  segments,
  initialItem,
  badgeAnimation,
}: TourListingCardProps) => {
  const itemImages = useMemo(() => {
    const items: SliderImage[] = [];
    images?.forEach((item) => items.push({ imageUrl: item, alt: 'setur' }));

    return items;
  }, [images]);
  const link = encodeURIComponent(`${process.env.NEXT_PUBLIC_HOST}/${url}`);
  const goToTourDetail = () =>
    !url?.includes('date')
      ? window.open(`/${url}`, '_blank', 'noopener, noreferrer')
      : window.open(
          `${process.env.NEXT_PUBLIC_HOST}${url}`,
          '_blank',
          'noopener, noreferrer'
        );

  const showBottomContainer = useMemo(() => {
    return badge?.some((item) => item.value === 'Misafirler Öneriyor');
  }, [badge]);
  const itemPromotions = useMemo(() => {
    const items: Array<CompanyPromotionItemProps> = [];

    if (!promotion || promotion?.length === 0) return [];

    promotion?.map((item) =>
      items.push({
        text: item.name,
        logo: item.image,
        description: item.description,
      })
    );

    return items;
  }, [promotion]);
  const videoAll = useMemo(() => {
    const tempVideos: string[] = [];
    videos?.map((item) => {
      if (item.url) tempVideos.push(item.url);
    });
    return tempVideos;
  }, [videos]);

  return (
    <DesktopWrapper>
      <BodyContainer>
        <DesktopStyledListingImageWrapper onClick={gtmClick}>
          <NextListingItemTourSlider
            tour={true}
            badge={badge?.some((item) => item.value === 'Misafirler Öneriyor')}
            images={itemImages || []}
            secure={secure}
            badgeAnimation={badgeAnimation}
            seturExecutive={seturExecutive}
            fallBackImage={fallBackImage}
            href={
              !url?.includes('date')
                ? `/${url}`
                : `${process.env.NEXT_PUBLIC_HOST}${url}`
            }
            imageHeight={340}
            name={name}
            video={videoAll ?? []}
            initialItem={initialItem}
          />
        </DesktopStyledListingImageWrapper>
        <TourDetailsDesktop>
          <ListingTitleWrapper>
            <ZoneWrapper>
              <TourDetailsItemDesktop>
                <ListingItemTimezone
                  icon={MapLocationPinIcon}
                  text={`${departureCityName}`}
                  iconSize={20}
                  iconColor='D400'
                  textColor='NL300'
                />
              </TourDetailsItemDesktop>

              {transportionTypeName ? (
                <TourDetailsItemDesktop>
                  <ListingItemTimezone
                    icon={getTransportationIcon(transportionTypeCode, true)}
                    text={transportionTypeName}
                    iconSize={20}
                    iconColor='D400'
                    textColor='NL300'
                    CustomIconComponent={() =>
                      getCustomTransportationIcon(transportionTypeCode, true)
                    }
                  />
                </TourDetailsItemDesktop>
              ) : null}

              <TourDetailsItemDesktop>
                <ListingItemTimezone
                  icon={DurationIcon}
                  text={`${nightStay} Gece, ${dayStay} Gün`}
                  iconSize={20}
                  iconColor='D400'
                  textColor='NL300'
                />
              </TourDetailsItemDesktop>
            </ZoneWrapper>
            <TourBoardTypeWrapper>
              {dates?.[0]?.departureDate ? (
                <Typography variant='caption2' color='NL800'>
                  {formatISOToDate(dates?.[0]?.departureDate)}
                  &apos;den itibaren
                </Typography>
              ) : null}
            </TourBoardTypeWrapper>
          </ListingTitleWrapper>
          <TourNameWrapperDesktop>
            <Link
              href={
                !url?.includes('date')
                  ? `/${url}`
                  : `${process.env.NEXT_PUBLIC_HOST}${url}`
              }
              target='_blank'
              title={name}
              prefetch={false}
            >
              <TourTitleDesktop onClick={gtmClick}>
                <Typography color='NL800' variant='caption' weight='SemiBold'>
                  {name}
                </Typography>
              </TourTitleDesktop>
            </Link>

            <ShareWrapperDesktop>
              <MyFavoriteComponent
                itemId={id}
                itemName={name}
                imageUrl={itemImages?.[0]?.imageUrl ?? ''}
                type={FavoriteItemType.TOUR}
                isListing={true}
                itemUrl={url?.split('?')[0]}
                favoriteLists={favoriteLists}
                refetchFavoriteLists={refetchFavoriteLists}
                defaultList={defaultList}
                defaultListItems={defaultListItems}
                refetchDefaultList={refetchDefaultList}
                fontSize={24}
                fontColor='D400'
              />
              <SocialShare iconSize={24} link={link} />
            </ShareWrapperDesktop>
          </TourNameWrapperDesktop>
          <ListingCardBodyWrapper>
            <PromotionWrapper>
              <BadgeWrapper>
                {/* <ListChippinCard /> */}
                {boardType === ABROAD_TOUR_TYPE ||
                boardType === TourType.CRUISE ? null : (
                  <WorldLogoWrapperDesktop>
                    <ListWorldLogo label={installments} />
                  </WorldLogoWrapperDesktop>
                )}
              </BadgeWrapper>
              <PromotionBottomContentWrapper>
                <TourPromotions>
                  {itemPromotions && itemPromotions.length ? (
                    <Promotions itemPromotions={itemPromotions} />
                  ) : null}
                </TourPromotions>
                {features && features.length > 0 ? (
                  <Features hightlights={features} />
                ) : null}
                <BottomWrapper>
                  <>
                    {segments
                      ? segments?.map((item, index) =>
                          item.typeName === 'Segment' ? (
                            <Segments key={index} segment={item} />
                          ) : null
                        )
                      : null}

                    {placesToGo ? (
                      <PlacesToGo placesItems={placesToGo} />
                    ) : null}
                    {dates?.length ? <AlternativeDates dates={dates} /> : null}
                  </>
                </BottomWrapper>
              </PromotionBottomContentWrapper>
            </PromotionWrapper>
            <DetailWrapper>
              <PriceWrapper>
                {discountRate ? (
                  <DiscountWrapperDesktop>
                    <DiscountLabel
                      discount={discountRate}
                      type={getDiscountType(discountType)}
                      currency={currency}
                    />
                  </DiscountWrapperDesktop>
                ) : null}

                {discounted ? (
                  <PriceInfoWrapperDesktop>
                    <PriceLabel
                      total={
                        originalPrice != discounted &&
                        (boardType === ABROAD_TOUR_TYPE ||
                          boardType === TourType.CRUISE)
                      }
                      priceWithDiscount={sellingPrice}
                      currency={getCurrencySymbol(currency)}
                      priceInEUR={
                        boardType === ABROAD_TOUR_TYPE ||
                        boardType === TourType.CRUISE
                          ? discounted
                          : undefined
                      }
                      originalPrice={originalPrice}
                      rate={rate}
                    />
                    <PriceSubLabel>‘den itibaren</PriceSubLabel>
                  </PriceInfoWrapperDesktop>
                ) : null}

                <DesktopButtonWrapper onClick={gtmClick}>
                  <Button style={{ borderRadius: 8 }} onClick={goToTourDetail}>
                    Turu Seç
                  </Button>
                </DesktopButtonWrapper>
              </PriceWrapper>
            </DetailWrapper>
          </ListingCardBodyWrapper>
        </TourDetailsDesktop>
      </BodyContainer>
      {showBottomContainer ? (
        <DesktopBottomContainer>
          {badge?.some((item) => item.value === 'Misafirler Öneriyor') ? (
            <Badge icon={InfoIcon} text='Misafirler Öneriyor' />
          ) : null}
        </DesktopBottomContainer>
      ) : null}
    </DesktopWrapper>
  );
};

export default DesktopTourListingCard;
